import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpErrorMessageHelper, SessionService } from '@edl-development/edl-common';
import { Transition } from '@uirouter/core';
import { ToastrService } from 'ngx-toastr';
import { SloStateTypes } from 'src/app/model/enum/slo-state.enum';
import { ObservationSummaryDTO } from 'src/app/model/observation/observation-summary-dto.model';
import { ObservationListService } from 'src/app/services/observation-list.service';
import { SloObservationService } from 'src/app/services/Observation/slo-observation.service';
import { ObservationAction } from './observation-actions.enum';
import { ModalService } from 'src/app/modals/modal.service';
import { ObservationDropdownAction } from 'src/app/model/observation/observation-dropdown-action.model';
import { ObservationSummaryPermissions } from 'src/app/model/observation/observation-summary-permissions.model';
import { PermissionsHelperService } from 'src/app/services/permissions-helper/permissions-helper.service';
import { UserSummaryPermissions } from 'src/app/model/observation/user-summary-permisions.model';
import { PaetepUserHelperService } from 'src/app/services/paetep-user-helper/paetep-user-helper.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-observation-actions',
    templateUrl: './observation-actions.component.html',
    styleUrls: ['./observation-actions.component.scss']
})
export class ObservationActionsComponent implements OnInit {
    @Input() observationSummary?: ObservationSummaryDTO;
    @Input() userSummaryPermissions?: UserSummaryPermissions;
    @Input() targetUserId?: string;
    @Input() allowedActions?: ObservationAction[];
    @Input() iconVisible: boolean = false;
    @Input() editEnabled: boolean = true;
    @Input() deleteEnabled: boolean = true;
    @Input() emailEnabled: boolean = true;
    @Input() printEnabled: boolean = true;
    @Input() attachmentsEnabled: boolean = false;
    @Input() attachmentsCount?: number = undefined;
    @Input() isInsideObservation: boolean = true;
    @Input() openNewTab: boolean = false;
    @Output() refreshListing = new EventEmitter();

    private observationActions: ObservationDropdownAction[] = [
        { Value: ObservationAction.OPEN_EDIT, Label: "Open/Edit", canUseInsideObs: false },
        { Value: ObservationAction.BLIND_EDIT, Label: "Blind View", canUseInsideObs: false, Params: { showTargetUser: false } },
        { Value: ObservationAction.NUDGE, canUseInsideObs: true, Label: "Nudge" },
        { Value: ObservationAction.PRINT, canUseInsideObs: true, Label: "Print" },
        { Value: ObservationAction.DUPLICATE, Label: 'Duplicate', canUseInsideObs: false },
        { Value: ObservationAction.REOPEN, Label: 'Reopen', canUseInsideObs: false },
        { Value: ObservationAction.CHANGE_SCHOOL_YEAR, canUseInsideObs: false, Label: "Change School Year" },
        // IMPORTANT: If canUseInsideObs changes to true for CHANGE_EVALUATOR, THEN userSummaryPermissions needs to be passed in at all locations
        { Value: ObservationAction.CHANGE_EVALUATOR, canUseInsideObs: false, Label: "Change Evaluator" },   
        { Value: ObservationAction.CHANGE_SLO_STATUS, Label: "Change SLO Status", canUseInsideObs: false },
        { Value: ObservationAction.DELETE, Label: "Delete", canUseInsideObs: false },
        { Value: ObservationAction.REINSTATE, Label: "Reinstate", canUseInsideObs: false },

    ];

    public filteredObservationActions: ObservationDropdownAction[] = [];

    observationActionsForm: FormGroup;

    constructor(
        public observationListService: ObservationListService,
        public transition: Transition,
        private permissionsHelperService: PermissionsHelperService,
        private sloObservationService: SloObservationService,
        private sessionService: SessionService,
        private toastr: ToastrService,
        private modalService: ModalService,
        private paetepUserHelperService: PaetepUserHelperService,
        private formBuilder: FormBuilder,
    ) {
        this.observationActionsForm = this.formBuilder.group({
            selectedAction: ['']
        });
    }

    get userId() {
        return this.targetUserId ?? this.transition.params<{ userId?: string }>().userId ?? '';
    }

    ngOnInit(): void {
        this.filteredObservationActions = this.filterAndSortActions(this.observationActions, this.isInsideObservation, this.observationSummary?.permissions, this.allowedActions);
    }

    changeObservationAction(actionId: string) {
        const params = this.observationActions?.find(a => a.Value === actionId)?.Params;

        switch (actionId) {
            case ObservationAction.OPEN_EDIT:
            case ObservationAction.BLIND_EDIT:
                this.editObservation(params);
                break;
            case ObservationAction.NUDGE: this.emailObservation(); break;
            case ObservationAction.PRINT: this.printObservation(); break;
            case ObservationAction.DUPLICATE: this.duplicateObservation(); break;
            case ObservationAction.REOPEN: this.reopenObservation(); break;
            case ObservationAction.CHANGE_SCHOOL_YEAR: this.changeSchoolYear(); break;
            case ObservationAction.CHANGE_EVALUATOR: this.changeEvaluator(); break;
            case ObservationAction.DELETE: this.deleteObservation(); break;
            case ObservationAction.REINSTATE: this.reinstateObservation(); break;
            case ObservationAction.CHANGE_SLO_STATUS: this.changeSloStatus(); break;
            default:
                this.toastr.error("Unrecognized action selected.", "Unrecognized Action");
        }

        this.reset();
    }

    reset() {
        this.observationActionsForm.controls['selectedAction'].setValue('');
    }

    editObservation(extraParams?: { [paramName: string]: any }) {
        if (this.observationSummary) {
            if (this.openNewTab) {
                const href = this.observationListService.getObservationLink(this.userId, this.observationSummary.observationId, this.observationSummary.itemType, false, undefined, extraParams);
                window.open(href, '_blank');
            } else {
                this.observationListService.openObservation(this.userId, this.observationSummary, false, undefined, extraParams);
            }
        }
    }

    printObservation() {
        this.toastr.info("Under Development", "Print");
    }

    emailObservation() {
        if (this.observationSummary) {
            this.modalService.openSendObservationNudge(
                this.observationSummary?.waitingOnId ?? this.userId,
                this.observationSummary.observationId,
                this.observationSummary.itemType,
                this.userId
            );
        }
    }

    openAttachments() {
        this.toastr.info("Under Development", "Attachments");
    }

    changeSchoolYear() {
        let schoolYears = (this.sessionService.GetSessionValue("school_years") as string[]).map(s => { return { label: s, value: s } });;

        this.modalService.dropdown("Change School Year", "School Year", this.observationSummary?.schoolYear, schoolYears)
            .then((schoolYear) => {
                this.observationListService
                    .updateSchoolYear(this.userId, this.observationSummary?.itemType as number, this.observationSummary?.observationId as string, schoolYear)
                    .subscribe({
                        next: (v: any) => {
                            this.refreshListing.emit(this.observationSummary?.observationId);
                        },
                        error: (err: any) => {
                            this.toastr.error(err);
                        },
                        complete: () => {
                            this.toastr.success("Observation School Year Changed.", "Success")
                        }
                    });
            })      
            .finally()
            .catch(()=>{});
    }

    changeEvaluator() {
        if (this.observationSummary) {
            const canCreateObservation = this.permissionsHelperService.canCreateObservation([this.observationSummary?.itemType], this.userSummaryPermissions);

            this.paetepUserHelperService.getSelectEvaluatorOptions(
                this.userId, 
                canCreateObservation,
                { userId: this.observationSummary?.evalUserId, displayName: this.observationSummary?.evalUserName }
            ).then((evaluators) => {
                this.modalService.dropdown("Change Evaluator", "Evaluator", this.observationSummary?.evalUserId, evaluators)
                    .then((evaluator) => {
                        firstValueFrom(
                            this.observationListService.updateEvaluator(this.userId, this.observationSummary?.itemType as number, this.observationSummary?.observationId as string, evaluator)
                        ).then(() => {
                            this.toastr.success("Observation Evaluator Changed.", "Success")
                            this.refreshListing.emit(this.observationSummary?.observationId);
                        });
                    })                                        
                    .finally()
                    .catch(()=>{});
            });
        }
    }

    deleteObservation() {
        this.modalService.confirm('Confirmation', 'Are you sure you want to delete this observation?')
            .then(() => {
                this.observationListService
                    .deleteObservation(this.userId, this.observationSummary?.itemType as number, this.observationSummary?.observationId as string)
                    .subscribe({
                        next: (v: any) => {
                            this.refreshListing.emit(this.observationSummary?.observationId);
                        },
                        error: (err: any) => {
                            this.toastr.error(err);
                        },
                        complete: () => {
                            this.toastr.success("Observation Deleted.", "Success")
                        }
                    });
            })      
            .finally()
            .catch(()=>{});
    }

    reinstateObservation() {
        this.modalService.confirm('Confirmation', 'Are you sure you want to reinstate this observation?')
            .then(() => {
                this.observationListService
                    .reinstateObservation(this.userId, this.observationSummary?.itemType as number, this.observationSummary?.observationId as string)
                    .subscribe({
                        next: (v: any) => {
                            this.refreshListing.emit(this.observationSummary?.observationId);
                        },
                        error: (err: any) => {
                            this.toastr.error(HttpErrorMessageHelper.getErrorMessage(err));
                        },
                        complete: () => {
                            this.toastr.success("Observation Reinstated.", "Success")
                        }
                    });
            })      
            .finally()
            .catch(()=>{});
    }

    duplicateObservation() {
        if (this.observationSummary && this.userSummaryPermissions) {
            this.modalService.openDuplicateObsModal(
                this.observationSummary.itemType, 
                this.observationSummary.observationId,
                this.userId,
                this.userSummaryPermissions
            );
        }
    }

    reopenObservation() {
        this.modalService.confirm('Confirmation', 'Are you sure you want to reopen this observation?')
            .then(() => firstValueFrom(
                this.observationListService
                    .reopenObservation(this.userId, this.observationSummary?.itemType as number, this.observationSummary?.observationId as string)
                ).then(() => {
                    this.toastr.success("Observation Reopened.", "Success");
                    this.refreshListing.emit(this.observationSummary?.observationId);
                })
            )      
            .finally()
            .catch(()=>{});
    }

    changeSloStatus() {
        let SLOStates = SloStateTypes.RollbackList.map(s => { return { value: s['Type'], label: s['Name'] } });
        this.modalService.dropdown("Change SLO State", "SLO State", SLOStates[0].value, SLOStates)
            .then((newState) => {
                this.sloObservationService
                    .rollbackSloState(this.observationSummary?.observationId as string, newState)
                    .subscribe({
                        next: (v: any) => {
                            this.refreshListing.emit(this.observationSummary?.observationId);
                        },
                        error: (err: any) => {
                            this.toastr.error(err);
                        },
                        complete: () => {
                            this.toastr.success("SLO State Changed.", "Success")
                        }
                    });
            })                        
            .finally()
            .catch(()=>{});
    }

    private filterAndSortActions(observationActions: ObservationDropdownAction[], isInsideObservation: boolean, permissions?: ObservationSummaryPermissions, allowedActions?: ObservationAction[]) {
        return observationActions
            .filter (a => (!isInsideObservation || a.canUseInsideObs) && this.hasPermission(a, permissions) && (!allowedActions || allowedActions.includes(a.Value)))
            .sort((a, b) => parseInt(a.Value) - parseInt(b.Value))
    }

    private hasPermission(observationActions: ObservationDropdownAction, permissions?: ObservationSummaryPermissions) {
        switch(observationActions.Value) {
            case ObservationAction.OPEN_EDIT:
                return permissions?.canView;
            case ObservationAction.BLIND_EDIT:
                return permissions?.canBlindView;
            case ObservationAction.NUDGE:
                return permissions?.canNudge;
            case ObservationAction.PRINT:
                return permissions?.canPrint;
            case ObservationAction.DUPLICATE:
                return permissions?.canDuplicate;
            case ObservationAction.REOPEN:
                return permissions?.canReopen;
            case ObservationAction.CHANGE_SCHOOL_YEAR:
                return permissions?.canChangeSchoolYear;
            case ObservationAction.CHANGE_EVALUATOR:
                return permissions?.canChangeEvaluator;
            case ObservationAction.DELETE:
                return permissions?.canDelete;
            case ObservationAction.REINSTATE:
                return permissions?.canReinstate;
            case ObservationAction.CHANGE_SLO_STATUS:
                return permissions?.canChangeSLOStatus;
            default:
                return false;
        }
    }
}
